import React, {
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
  useContext,
  useMemo,
} from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { getAddressSuggestions } from "api";
import { AddressSuggestion, Location } from "types";
import { debounce } from "@mui/material/utils";
import { ToastContext, TOAST_TYPE } from "context/toastContext";
import { useTranslation } from "react-i18next";

export interface AddressAutocompleteProps {
  setLocation: (address: Location) => void;
  defaultOption?: AddressSuggestion | undefined;
  variant?: "standard" | "outlined" | "filled";
  margin?: "none" | "dense" | "normal";
  label?: string;
}

export default function AddressAutocomplete({
  setLocation,
  defaultOption,
  variant = "outlined",
  margin = "none",
  label = "Address",
}: AddressAutocompleteProps) {
  const displayToast = useContext(ToastContext);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<readonly AddressSuggestion[]>(
    defaultOption ? [defaultOption] : [],
  );
  const [address, setAddress] = useState<AddressSuggestion | null>(null);
  const [inputValue, setInputValue] = useState("");
  const loading = open && options.length === 0;

  const fetchSuggestions = async (inputValue: string) => {
    try {
      const res = await getAddressSuggestions(inputValue);
      if (res.ok) {
        const data = await res.json();
        if (inputValue.length > 3 || !defaultOption) {
          setOptions(data.suggestions);
        } else {
          setOptions([defaultOption, ...data.suggestions]);
        }
      } else {
        throw new Error(`${res.status} ${res.statusText}`);
      }
    } catch (e) {
      const error = e as Error;
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: error.message,
      });
    }
  };

  const fetchRef = useRef(fetchSuggestions);
  fetchRef.current = fetchSuggestions;

  const debouncedFetch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.length > 3) {
          fetchRef.current(value);
        }
      }, 400),
    [],
  );

  useEffect(() => {
    debouncedFetch(inputValue);

    return () => {
      debouncedFetch.clear();
    };
  }, [inputValue, debouncedFetch]);

  const parseAddress = (
    address: AddressSuggestion | null,
  ): Location | undefined => {
    if (!address) return;
    let addressText = address.text;
    if (address.magicKey === "default_option") {
      addressText = addressText.split(":")[1];
    }
    const addressParts = addressText.split(",");
    return {
      street: addressParts[0].trim(),
      city: addressParts[1].trim(),
      stateCode: addressParts[2].trim(),
      postalCode: addressParts[3].trim(),
      country: addressParts[4].trim(),
    };
  };

  const handleOnChange = (
    e: SyntheticEvent,
    address: AddressSuggestion | null,
  ) => {
    setAddress(address);
    const parsedAddress = parseAddress(address);
    if (parsedAddress) {
      setLocation(parsedAddress);
      setInputValue("");
      setAddress(null);
    }
  };

  const handleOnInputChange = (e: SyntheticEvent, value: string) => {
    setInputValue(value);
  };

  return (
    <Autocomplete
      id="address-autocomplete"
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      includeInputInList
      filterSelectedOptions
      isOptionEqualToValue={(option, value) => {
        return option.text === value.text;
      }}
      getOptionLabel={(option) => option.text}
      options={options}
      value={address}
      fullWidth
      blurOnSelect
      noOptionsText={t("noOptions", { name: t("address") })}
      loading={loading}
      loadingText={t("searching")}
      filterOptions={(x) => x}
      onInputChange={handleOnInputChange}
      onChange={handleOnChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={variant}
          margin={margin}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
}
